import { useState, useRef, useEffect, useCallback, useReducer, forwardRef, useImperativeHandle } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { AiContainedIcon } from '@frontend/styled-icons';
import { theme } from '@frontend/theme';
import { NakedButton, Text } from '@frontend/design-system';
import { base64ToArrayBuffer, convertFloat32ToInt16 } from './audio-utils';
import { ConversationTranscript, type TranscriptTurn } from './conversation-transcript';
import { useVoiceActivityDetection } from './use-voice-activity-detection';

const SAMPLE_RATE = 8000; // 8kHz sample rate for PCM audio

type CallState = {
  transcripts: TranscriptTurn[];
  hasStartedCall: boolean;
  isAISpeaking: boolean;
  isUserSpeaking: boolean;
  showYourTurn: boolean;
  callEnded: boolean;
};

type CallAction =
  | { type: 'CALL_CONNECTED' }
  | { type: 'TRANSCRIPT_UPDATE'; transcripts: TranscriptTurn[] }
  | { type: 'AI_SPEAKING_CHANGE'; isSpeaking: boolean }
  | { type: 'USER_SPEAKING_CHANGE'; isSpeaking: boolean }
  | { type: 'SESSION_END' };

const initialCallState: CallState = {
  transcripts: [],
  hasStartedCall: false,
  isAISpeaking: false,
  isUserSpeaking: false,
  showYourTurn: false,
  callEnded: false,
};

function callStateReducer(state: CallState, action: CallAction): CallState {
  switch (action.type) {
    case 'CALL_CONNECTED':
      return {
        ...state,
        hasStartedCall: true,
        transcripts: [],
        callEnded: false,
      };
    case 'TRANSCRIPT_UPDATE':
      return {
        ...state,
        transcripts: [...state.transcripts, ...action.transcripts],
      };
    case 'AI_SPEAKING_CHANGE':
      return {
        ...state,
        isAISpeaking: action.isSpeaking,
        showYourTurn: !action.isSpeaking, // Show "your turn" when AI finishes
      };
    case 'USER_SPEAKING_CHANGE':
      return {
        ...state,
        isUserSpeaking: action.isSpeaking,
        showYourTurn: action.isSpeaking ? false : state.showYourTurn, // Hide "your turn" when user speaks
      };
    case 'SESSION_END':
      return {
        ...state,
        isAISpeaking: false,
        isUserSpeaking: false,
        showYourTurn: false,
        callEnded: true,
      };
    default:
      return state;
  }
}

export interface VoiceComponentHandle {
  disconnect: () => void;
}

export interface VoiceComponentProps {
  websocketUrl: string;
  onError?: (error: Error) => void;
  onConnectionChange?: (connected: boolean) => void;
  onSessionEnd?: (reason: string, intent?: string) => void;
  onAITurnStart?: () => void; // Called when AI starts speaking
  onAITurnEnd?: () => void; // Called when AI finishes speaking
  onTranscriptUpdate?: (transcripts: TranscriptTurn[]) => void; // Called when transcripts change
  onAISpeakingChange?: (isSpeaking: boolean) => void; // Called when AI speaking state changes
  onUserSpeakingChange?: (isSpeaking: boolean) => void; // Called when user speaking state changes
  isAISpeaking?: boolean; // Current AI speaking state from parent
  isUserSpeaking?: boolean; // Current user speaking state from parent
  disabled?: boolean;
  enableVAD?: boolean; // Enable Voice Activity Detection
  silenceThreshold?: number; // Custom silence threshold (default: 5)
  silenceDuration?: number; // Custom silence duration in ms (default: 1500)
  debugMode?: boolean; // Show real-time audio levels for tuning
}

const containerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing(2)};
  padding: ${theme.spacing(4)};
  height: 100%;
  width: 100%;
`;

const micButtonStyle = (isRecording: boolean) => css`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  background: ${isRecording ? theme.colors.critical50 : theme.colorsV2.green50()};
  border: 4px solid ${isRecording ? theme.colors.critical70 : theme.colorsV2.green70()};
  cursor: pointer;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export const VoiceComponent = forwardRef<VoiceComponentHandle, VoiceComponentProps>(
  (
    {
      websocketUrl,
      onError,
      onConnectionChange,
      onSessionEnd,
      onAITurnStart,
      onAITurnEnd,
      onTranscriptUpdate,
      onAISpeakingChange,
      onUserSpeakingChange,
      isAISpeaking = false,
      isUserSpeaking = false,
      disabled = false,
      enableVAD = true,
      silenceThreshold = 5,
      silenceDuration = 1500,
      debugMode = false,
    },
    ref
  ) => {
    const [isRecording, setIsRecording] = useState(false);
    const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
    const [audioLevel, setAudioLevel] = useState(0);

    const wsRef = useRef<WebSocket | null>(null);
    const audioStreamRef = useRef<MediaStream | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const nextStartTimeRef = useRef(0);
    const audioWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
    const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
    const activeAudioSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
    const aiSilenceTimeoutRef = useRef<number | null>(null);
    const isAISpeakingRef = useRef(false);

    // Helper function to add transcript - must be defined before VAD callbacks
    const addTranscript = useCallback(
      (speaker: 'user' | 'ai', text: string, isPartial = false) => {
        const newTranscript: TranscriptTurn = {
          id: `${Date.now()}-${speaker}`,
          speaker,
          text,
          timestamp: new Date(),
          isPartial,
        };

        // Parent manages transcript array via reducer
        onTranscriptUpdate?.([newTranscript]);
      },
      [onTranscriptUpdate]
    );

    // Stable callbacks for VAD to prevent effect from re-running
    const handleSpeechStart = useCallback(() => {
      console.log('User started speaking');
      onUserSpeakingChange?.(true);
    }, [onUserSpeakingChange]);

    const handleSpeechEnd = useCallback(() => {
      console.log('User stopped speaking');
      onUserSpeakingChange?.(false);

      // For testing: Add a mock user transcript when user stops speaking
      if (debugMode) {
        const mockUserText = `User test message at ${new Date().toLocaleTimeString()}`;
        addTranscript('user', mockUserText);
      }
    }, [debugMode, addTranscript, onUserSpeakingChange]);

    const handleAudioLevel = useCallback(
      (level: number) => {
        // console.log('Audio level:', level.toFixed(2), '| Threshold:', silenceThreshold);
        setAudioLevel(level);
      },
      [setAudioLevel]
    );

    // Keep ref in sync with state
    useEffect(() => {
      audioStreamRef.current = audioStream;
    }, [audioStream]);

    // Voice Activity Detection for user speech
    useVoiceActivityDetection({
      stream: audioStream,
      audioContext: audioContextRef.current,
      enabled: isRecording && enableVAD,
      silenceThreshold,
      silenceDuration,
      debugMode,
      onSpeechStart: handleSpeechStart,
      onSpeechEnd: handleSpeechEnd,
      onAudioLevel: handleAudioLevel,
    });

    // Initialize AudioContext
    useEffect(() => {
      return () => {
        if (audioContextRef.current) {
          audioContextRef.current.close();
        }
        if (aiSilenceTimeoutRef.current) {
          window.clearTimeout(aiSilenceTimeoutRef.current);
        }
      };
    }, []);

    const handleTranscript = useCallback(
      (data: { role: string; text: string }) => {
        const speaker = data.role === 'assistant' ? 'ai' : 'user';
        addTranscript(speaker, data.text);
      },
      [addTranscript]
    );

    const handleStreamAudio = useCallback(
      async ({ audioData, sampleRate }: { audioData: string; audioDataType: string; sampleRate: number }) => {
        if (!audioContextRef.current) return;

        // Track that AI is speaking
        if (!isAISpeakingRef.current) {
          isAISpeakingRef.current = true;
          onAITurnStart?.();
          onAISpeakingChange?.(true);

          // For testing: Add a mock AI transcript when AI starts speaking
          if (debugMode) {
            const mockAIText = `AI started speaking at ${new Date().toLocaleTimeString()}`;
            addTranscript('ai', mockAIText);
          }
        }

        try {
          const arrayBuffer = base64ToArrayBuffer(audioData);
          const int16Data = new Int16Array(arrayBuffer);
          const float32Data = new Float32Array(int16Data.length);

          // Convert Int16 PCM to Float32 for Web Audio API
          for (let i = 0; i < int16Data.length; i++) {
            float32Data[i] = int16Data[i] / 0x7fff;
          }

          const buffer = audioContextRef.current.createBuffer(1, float32Data.length, sampleRate);
          buffer.getChannelData(0).set(float32Data);

          // Resume AudioContext if suspended (required by browsers after user interaction)
          if (audioContextRef.current.state === 'suspended') {
            console.log('Resuming suspended AudioContext...');
            await audioContextRef.current.resume();
            console.log('AudioContext resumed, state:', audioContextRef.current.state);
          }

          const sourceNode = audioContextRef.current.createBufferSource();
          sourceNode.buffer = buffer;
          sourceNode.connect(audioContextRef.current.destination);

          // Track this audio source
          activeAudioSourcesRef.current.add(sourceNode);

          // Clear any existing silence timeout when new audio arrives
          if (aiSilenceTimeoutRef.current) {
            window.clearTimeout(aiSilenceTimeoutRef.current);
            aiSilenceTimeoutRef.current = null;
          }

          // When this audio chunk finishes playing
          sourceNode.onended = () => {
            activeAudioSourcesRef.current.delete(sourceNode);

            // If no more audio sources are playing, start silence detection
            if (activeAudioSourcesRef.current.size === 0 && isAISpeakingRef.current) {
              // Wait a bit to see if more audio comes in
              // If no audio arrives in 500ms, consider AI done speaking
              aiSilenceTimeoutRef.current = window.setTimeout(() => {
                if (activeAudioSourcesRef.current.size === 0) {
                  isAISpeakingRef.current = false;
                  onAITurnEnd?.();
                  onAISpeakingChange?.(false);
                  console.log('AI turn complete (detected locally via silence)');

                  // For testing: Simulate user response after AI finishes
                  if (debugMode) {
                    // Simulate AI responding back

                    const mockAIText = `AI ends speaking at ${new Date().toLocaleTimeString()}`;
                    addTranscript('ai', mockAIText);
                  }
                }
              }, 500);
            }
          };

          // Schedule playback to avoid gaps
          const startTime = Math.max(audioContextRef.current.currentTime, nextStartTimeRef.current);
          sourceNode.start(startTime);
          nextStartTimeRef.current = startTime + buffer.duration;
        } catch (error) {
          console.error('Error processing audio:', error);
          onError?.(error as Error);
        }
      },
      [onError, onAITurnStart, onAITurnEnd, onAISpeakingChange, debugMode, addTranscript]
    );

    const connectWebSocket = () => {
      try {
        const ws = new WebSocket(websocketUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          onConnectionChange?.(true);
        };

        ws.onclose = () => {
          onConnectionChange?.(false);
        };

        ws.onerror = (_error) => {
          const err = new Error('WebSocket error');
          onError?.(err);
        };

        ws.onmessage = async (event) => {
          try {
            const message = JSON.parse(event.data);

            const isTranscript = 'transcript' in message;

            if (isTranscript) {
              handleTranscript(message.transcript);
              return;
            }

            const messageType = message.type;

            // Handle session end events
            if (['endCall', 'route', 'textHandoff', 'fatal'].includes(messageType)) {
              const reason = message.data?.reason || 'Unknown reason';
              const intent = message.data?.caller_intent;

              if (messageType === 'fatal') {
                console.error(`Session Ended (${messageType})`, reason, intent);
                onError?.(new Error(`Fatal error: ${reason}`));
              }

              // Clear AI speaking state and call onSessionEnd FIRST to prevent "your turn" flash
              if (isAISpeakingRef.current) {
                isAISpeakingRef.current = false;
              }

              onSessionEnd?.(reason, intent);

              // Stop recording and close WebSocket
              if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
              }

              stopRecording();
              return;
            }

            switch (messageType) {
              case 'streamAudio':
                if (message.data?.audioData) {
                  console.log('Receiving audio data, length:', message.data.audioData.length);
                  handleStreamAudio(message.data);
                }
                break;

              // Placeholder for transcript messages - update when actual format is known
              case 'transcript':
              case 'aiTranscript':
                if (message.data?.text) {
                  console.log('Received AI transcript:', message.data.text);
                  addTranscript('ai', message.data.text, message.data?.isPartial);
                }
                break;

              case 'userTranscript':
                if (message.data?.text) {
                  console.log('Received user transcript:', message.data.text);
                  addTranscript('user', message.data.text, message.data?.isPartial);
                }
                break;

              default:
                console.warn('Unknown message type:', messageType, message);
            }
          } catch (error) {
            console.error('Failed to parse message:', error);
            onError?.(error as Error);
          }
        };
      } catch (error) {
        onError?.(error as Error);
      }
    };

    useEffect(() => {
      // Clean up WebSocket connection on unmount
      return () => {
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const stopRecording = useCallback(() => {
      // Stop audio stream tracks first
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((track) => track.stop());
      }

      // Stop audio processing nodes
      if (audioWorkletNodeRef.current) {
        audioWorkletNodeRef.current.port.close();
        audioWorkletNodeRef.current.disconnect();
        audioWorkletNodeRef.current = null;
      }

      if (scriptProcessorRef.current) {
        scriptProcessorRef.current.disconnect();
        scriptProcessorRef.current.onaudioprocess = null;
        scriptProcessorRef.current = null;
      }

      setIsRecording(false);
      setAudioStream(null);
      audioStreamRef.current = null;
      onUserSpeakingChange?.(false);
    }, [onUserSpeakingChange]);

    const startRecording = async () => {
      if (disabled) return;

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({
            sampleRate: SAMPLE_RATE,
          });
        }

        const source = audioContextRef.current.createMediaStreamSource(stream);

        try {
          // Try to use modern AudioWorklet API
          // Import the worklet file - Vite will handle bundling
          const workletUrl = new URL('./audio-processor.worklet.js', import.meta.url).href;
          console.log('Loading AudioWorklet from:', workletUrl);
          await audioContextRef.current.audioWorklet.addModule(workletUrl);

          const workletNode = new AudioWorkletNode(audioContextRef.current, 'audio-capture-processor');
          audioWorkletNodeRef.current = workletNode;

          console.log('AudioWorklet initialized successfully');

          workletNode.port.onmessage = (event) => {
            if (event.data.type === 'audioData') {
              const float32Data = event.data.data as Float32Array;

              // Convert Float32 to Int16 PCM
              const int16Buffer = convertFloat32ToInt16(float32Data);

              // Send to WebSocket if connected
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(int16Buffer.buffer);
              } else {
                console.warn('WebSocket not ready, state:', wsRef.current?.readyState);
              }
            }
          };

          // Connect the audio graph
          source.connect(workletNode);
          // Connect to a muted gain node to keep processing active without audio output
          const gainNode = audioContextRef.current.createGain();
          gainNode.gain.value = 0; // Mute the output
          workletNode.connect(gainNode);
          gainNode.connect(audioContextRef.current.destination);

          console.log('AudioWorklet connected successfully');
        } catch (workletError) {
          console.warn('AudioWorklet not supported, falling back to ScriptProcessorNode:', workletError);

          // Fallback to ScriptProcessorNode for older browsers
          const processor = audioContextRef.current.createScriptProcessor(4096, 1, 1);
          scriptProcessorRef.current = processor;

          processor.onaudioprocess = (event) => {
            const inputData = event.inputBuffer.getChannelData(0);

            // Convert Float32 to Int16 PCM
            const int16Buffer = convertFloat32ToInt16(inputData);

            // Send to WebSocket if connected
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(int16Buffer.buffer);
            }
          };

          // Connect the audio graph
          source.connect(processor);
          // Use a muted gain node to avoid hearing yourself
          const gainNode = audioContextRef.current.createGain();
          gainNode.gain.value = 0; // Mute the output
          processor.connect(gainNode);
          gainNode.connect(audioContextRef.current.destination);

          console.log('ScriptProcessorNode connected successfully');
        }

        setIsRecording(true);
        setAudioStream(stream);
      } catch (error) {
        onError?.(error as Error);
      }
    };

    const handleMicButtonPress = () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      } else {
        connectWebSocket();
      }

      if (isRecording) {
        stopRecording();
      } else {
        startRecording();
      }
    };

    // Expose imperative handle for programmatic disconnect
    useImperativeHandle(ref, () => ({
      disconnect: () => {
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
        stopRecording();
        onSessionEnd?.('Settings changed', undefined);
      },
    }));

    // Stop recording when component unmounts
    useEffect(() => {
      return () => {
        stopRecording();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <div css={containerStyle}>
        {isRecording && debugMode && (
          <>
            <Text weight='bold' css={{ marginBottom: theme.spacing(2) }}>
              {isAISpeaking ? '🤖 AI Speaking...' : isUserSpeaking ? '🎤 You are speaking...' : '👂 Listening...'}
            </Text>
            <Text css={{ marginBottom: theme.spacing(1), fontSize: '12px', color: theme.colorsV2.neutral60() }}>
              Audio Level: {audioLevel.toFixed(2)} | Threshold: {silenceThreshold}
            </Text>
          </>
        )}
        <NakedButton
          css={micButtonStyle(isRecording)}
          onClick={handleMicButtonPress}
          // disabled={disabled || !isConnected}
          aria-label={isRecording ? 'Stop recording' : 'Start recording'}
        >
          {isRecording ? (
            <Icon name='phone-hangup' size={40} color='white' />
          ) : (
            <Icon name='phone' size={40} color='white' />
          )}
        </NakedButton>
      </div>
    );
  }
);

VoiceComponent.displayName = 'VoiceComponent';

export interface VoiceInterfaceHandle {
  endCall: () => void;
}

export const VoiceInterface = forwardRef<
  VoiceInterfaceHandle,
  {
    wsUrl: string;
    settingsChangedBanner?: string | null;
  }
>(({ wsUrl, settingsChangedBanner }, ref) => {
  const [callState, dispatch] = useReducer(callStateReducer, initialCallState);
  const voiceComponentRef = useRef<VoiceComponentHandle>(null);

  const handleConnectionChange = (connected: boolean) => {
    if (connected) {
      dispatch({ type: 'CALL_CONNECTED' });
    }
  };

  const handleTranscriptUpdate = (newTranscripts: TranscriptTurn[]) => {
    dispatch({ type: 'TRANSCRIPT_UPDATE', transcripts: newTranscripts });
  };

  const handleAISpeakingChange = (isSpeaking: boolean) => {
    dispatch({ type: 'AI_SPEAKING_CHANGE', isSpeaking });
  };

  const handleUserSpeakingChange = (isSpeaking: boolean) => {
    dispatch({ type: 'USER_SPEAKING_CHANGE', isSpeaking });
  };

  const handleSessionEnd = (reason: string, intent?: string) => {
    console.log('Session ended:', reason, intent);
    dispatch({ type: 'SESSION_END' });
  };

  // Expose imperative handle for parent to end call programmatically
  useImperativeHandle(ref, () => ({
    endCall: () => {
      // Disconnect WebSocket and stop recording first
      voiceComponentRef.current?.disconnect();
      // Then update UI state
      dispatch({ type: 'SESSION_END' });
    },
  }));

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: '1fr auto auto',
        height: '100%',
        border: `1px solid ${theme.colorsV2.neutral20()}`,
        borderRadius: theme.borderRadius.medium,
      }}
    >
      {!callState.hasStartedCall ? (
        <EmptyTranscriptState />
      ) : (
        <ConversationTranscript
          transcripts={callState.transcripts}
          isAISpeaking={callState.isAISpeaking}
          isUserSpeaking={callState.isUserSpeaking}
          showYourTurn={callState.showYourTurn}
          callEnded={callState.callEnded}
          settingsChangedBanner={settingsChangedBanner}
        />
      )}
      <div style={{ width: '100%', height: 1, background: theme.colorsV2.neutral20() }} />
      <VoiceComponent
        ref={voiceComponentRef}
        websocketUrl={wsUrl}
        onTranscriptUpdate={handleTranscriptUpdate}
        onConnectionChange={handleConnectionChange}
        onAISpeakingChange={handleAISpeakingChange}
        onUserSpeakingChange={handleUserSpeakingChange}
        onSessionEnd={handleSessionEnd}
        isAISpeaking={callState.isAISpeaking}
        isUserSpeaking={callState.isUserSpeaking}
      />
    </div>
  );
});

VoiceInterface.displayName = 'VoiceInterface';

const EmptyTranscriptState = () => {
  const { t } = useTranslation('ai-receptionist');
  return (
    <div
      style={{
        textAlign: 'center',
        padding: theme.spacing(4),
        display: 'grid',
        gridTemplateRows:
          '[first-start] auto [first-end] 16px [second-start] auto [second-end] 8px [third-start] auto [third-end]',
        justifyItems: 'center',
        alignItems: 'center',
        alignContent: 'center',
      }}
    >
      <div style={{ gridRow: 'first-start / first-end' }}>
        <AiContainedIcon iconName='aiweave-assistant' size={124} />
      </div>
      <Text css={{ gridRow: 'second-start / second-end' }} weight='bold'>
        {t('Click the call button to start a simulated call with the AI Receptionist')}
      </Text>
      <Text css={{ gridRow: 'third-start / third-end' }}>
        {t(
          "You'll hear how it would respond to patients using the information you've saved in the AI Receptionist's settings."
        )}
      </Text>
    </div>
  );
};
